import { Midi } from "@tonejs/midi";
import type { NoteEvent, Song } from "../types";

// MIDI loader/parser. @tonejs/midi resolves the tempo map and
// returns notes with onset/duration already in seconds, per track — we consume
// that note model rather than re-implementing SMF parsing. Hand assignment is
// done downstream by the Engine (see engine/hand-split.ts).

/** Minimal view of @tonejs/midi's header that the bar grid needs. */
interface GridHeader {
  ppq: number;
  timeSignatures: { ticks: number; timeSignature: number[] }[];
  ticksToSeconds(ticks: number): number;
}

/**
 * Barline onset times in seconds, ascending; `bars[0] = 0` and the final entry
 * is the song end. Walks the time-signature map tick 0 → `lastTick`, emitting a
 * barline per measure; falls back to a synthetic 4/4 grid when no meter is set.
 * Pure (takes a header view + last tick) so it is unit-testable.
 */
export function barsFromGrid(header: GridHeader, lastTick: number): number[] {
  const end = Number.isFinite(lastTick) && lastTick > 0 ? lastTick : 0;
  const sigs = header.timeSignatures.length
    ? [...header.timeSignatures].sort((a, b) => a.ticks - b.ticks)
    : [{ ticks: 0, timeSignature: [4, 4] }];
  const bars: number[] = [];
  let tick = 0;
  let i = 0;
  while (tick < end && bars.length < 100000) {
    bars.push(header.ticksToSeconds(tick));
    while (i + 1 < sigs.length) {
      const next = sigs[i + 1];
      if (next && next.ticks <= tick) i++;
      else break;
    }
    const sig = sigs[i];
    const num = sig?.timeSignature[0] ?? 4;
    const den = sig?.timeSignature[1] ?? 4;
    tick += Math.max(1, Math.round((header.ppq * 4 * num) / den));
  }
  bars.push(header.ticksToSeconds(Math.max(tick, end)));
  return bars;
}

/**
 * Per-beat onset times in seconds (the metronome grid), ascending from 0. Walks
 * the time-signature map stepping one denominator-unit at a time, so a barline is
 * always also a beat (downbeats are a subset of `bars`). Falls back to a 120 BPM
 * quarter grid when no meter is set. Pure (header view + last tick).
 */
export function beatsFromGrid(header: GridHeader, lastTick: number): number[] {
  const end = Number.isFinite(lastTick) && lastTick > 0 ? lastTick : 0;
  const sigs = header.timeSignatures.length
    ? [...header.timeSignatures].sort((a, b) => a.ticks - b.ticks)
    : [{ ticks: 0, timeSignature: [4, 4] }];
  const beats: number[] = [];
  let tick = 0;
  let i = 0;
  while (tick < end && beats.length < 400000) {
    beats.push(header.ticksToSeconds(tick));
    while (i + 1 < sigs.length) {
      const next = sigs[i + 1];
      if (next && next.ticks <= tick) i++;
      else break;
    }
    const den = sigs[i]?.timeSignature[1] ?? 4;
    tick += Math.max(1, Math.round((header.ppq * 4) / den));
  }
  return beats;
}

/** Parse a Standard MIDI File (format 0 or 1) into the timed note model. */
export function parseMidi(buffer: ArrayBuffer, name: string): Song {
  const midi = new Midi(buffer);
  // Reject non-metrical timing: ppq = 0 divides by zero (NaN times); SMPTE files
  // leave ppq undefined. Either yields a song that "loads" but never falls/gates.
  if (!Number.isFinite(midi.header.ppq) || midi.header.ppq <= 0) {
    throw new Error("Unsupported MIDI time division — needs metrical (PPQ) timing.");
  }
  const notes: NoteEvent[] = [];

  midi.tracks.forEach((track, trackIndex) => {
    for (const n of track.notes) {
      notes.push({
        midi: n.midi,
        onset: n.time,
        duration: n.duration,
        group: trackIndex,
        hand: "unknown",
        velocity: n.velocity,
      });
    }
  });

  let duration = 0;
  for (const n of notes) duration = Math.max(duration, n.onset + n.duration);

  notes.sort((a, b) => a.onset - b.onset);
  const bars = barsFromGrid(midi.header, midi.durationTicks);
  const beats = beatsFromGrid(midi.header, midi.durationTicks);
  return { name, notes, duration, bars, beats };
}
